// src/pages/CreateTrial.jsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { supabase } from '../supabaseClient';
import CommunityRulesPanel from '../components/CommunityRulesPanel';
import { recordReferralMilestone } from '../utils/referrals';
import { updateStreakForActivity } from '../utils/streaks';
import { safeStorage } from '../utils/safeStorage';

const RECENT_CREATED_TRIAL_KEY = 'gweffit_recent_created_trial';

// Beta mode flag - should match App.js
const BETA_MODE = true;

// Duration options for beta testing
const DURATION_OPTIONS = [
  { value: '2min', label: '2 minutes', seconds: 120, description: 'Real-time (need friends online now)' },
  { value: '15min', label: '15 minutes', seconds: 900, description: 'Quick async' },
  { value: '1hour', label: '1 hour', seconds: 3600, description: 'Time to text your group chat' },
  { value: '4hours', label: '4 hours', seconds: 14400, description: 'Half-day debate' },
  { value: '24hours', label: '24 hours', seconds: 86400, description: 'Full day to gather votes' },
];

const BLOCKED_PHRASES = [
  'kill yourself',
  'kys',
  'nazi',
  'slur',
  'rape',
  'pedo',
  'porn',
  'nudes',
  'faggot',
  'retard',
];

function containsBlockedPhrase(text) {
  const normalized = String(text || '').toLowerCase();
  return BLOCKED_PHRASES.find((phrase) => normalized.includes(phrase)) || null;
}

function looksLikePersonalAttack(title, sideA, sideB) {
  const combined = `${title} ${sideA} ${sideB}`.toLowerCase().trim();
  return /\bis\s+[a-z][a-z]+(\s+[a-z][a-z]+)?\s+(ugly|stupid|fat|worthless|trash)\b/.test(combined);
}

async function verifyCreatedTrialExists(trialId, { authUserId = null, appUserId = null } = {}) {
  const attempts = 6;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const { data, error } = await supabase
      .from('trials')
      .select('id, creator_id, created_by, status, created_at')
      .eq('id', trialId)
      .maybeSingle();

    if (!error && data?.id) {
      const creatorMatches =
        (!appUserId && !authUserId)
        || (appUserId && String(data.created_by || '') === String(appUserId))
        || (appUserId && String(data.creator_id || '') === String(appUserId))
        || (authUserId && String(data.creator_id || '') === String(authUserId));
      if (creatorMatches) {
        return { ok: true, row: data };
      }
    }

    if (attempt < attempts) {
      await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    }
  }

  return { ok: false };
}

function persistRecentCreatedTrial(trialId) {
  try {
    safeStorage.set(
      RECENT_CREATED_TRIAL_KEY,
      JSON.stringify({
        trialId: String(trialId || ''),
        createdAt: Date.now(),
      }),
    );
  } catch {}
}

export default function CreateTrial() {
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [sideA, setSideA] = useState('');
  const [sideB, setSideB] = useState('');
  const [creatorSide, setCreatorSide] = useState('');
  const [emoji, setEmoji] = useState('⚖️');
  const [duration, setDuration] = useState(BETA_MODE ? '1hour' : '2min'); // Default to 1 hour in beta
  const [saving, setSaving] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Trial mode: debate vs decide-for-me (personal decisions)
  const [trialMode, setTrialMode] = useState('debate'); // 'debate' | 'decide_for_me'
  const [decisionCategory, setDecisionCategory] = useState('');

  const EMOJIS = ['⚖️','🔥','👑','🎮','🍕','⚽','🎬','🎵','💰','🚀','👀','🤔'];

  const DECIDE_FOR_ME_TEMPLATES = [
    'Should I text my ex?',
    'Should I quit my job?',
    'Should I get bangs?',
    'Should I move to a new city?',
    'Should I buy it?',
    'Should I take the trip?',
    'Should I apologize?',
    'Should I end it?',
  ];

  const DECISION_CATEGORIES = [
    { value: '', label: 'Optional: choose a category' },
    { value: 'dating', label: 'Dating' },
    { value: 'work', label: 'Work' },
    { value: 'style', label: 'Style' },
    { value: 'money', label: 'Money' },
    { value: 'friends', label: 'Friends' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const presetTitle = params.get('title');
    const presetSideA = params.get('sideA');
    const presetSideB = params.get('sideB');
    const presetEmoji = params.get('emoji');
    const presetMode = params.get('mode');
    const presetCategory = params.get('category');

    setCreatorSide('');
    if (presetTitle) setTitle(presetTitle);
    if (presetSideA) setSideA(presetSideA);
    if (presetSideB) setSideB(presetSideB);
    if (presetEmoji) setEmoji(presetEmoji);
    if (presetMode === 'debate' || presetMode === 'decide_for_me') setTrialMode(presetMode);
    if (presetCategory) setDecisionCategory(presetCategory);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;

        if (!session?.user) {
          safeStorage.set('gweffit_return_url', '/create');
          window.location.href = '/?auth=signup&auth_context=create_trial&auth_message=Enter%20your%20email%20and%20we%27ll%20send%20a%206-digit%20code%20to%20post%20your%20trial.';
          return;
        }

        setAuthChecked(true);
      } catch {
        if (cancelled) return;
        safeStorage.set('gweffit_return_url', '/create');
        window.location.href = '/?auth=signup&auth_context=create_trial&auth_message=Enter%20your%20email%20and%20we%27ll%20send%20a%206-digit%20code%20to%20post%20your%20trial.';
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Get duration in seconds
  const getDurationSeconds = () => {
    const option = DURATION_OPTIONS.find(o => o.value === duration);
    return option ? option.seconds : 120;
  };

  function switchTrialMode(nextMode) {
    setTrialMode(nextMode);
    setCreatorSide('');

    if (nextMode === 'decide_for_me') {
      // Helpful defaults for decision-style trials
      if (!sideA.trim() && !sideB.trim()) {
        setSideA('Do it');
        setSideB("Don't do it");
      }
      if (emoji === '⚖️') setEmoji('🤔');
    } else {
      // If the user never customized beyond the defaults, clear back to debate blank
      if (sideA.trim() === 'Do it' && sideB.trim() === "Don't do it") {
        setSideA('');
        setSideB('');
      }
      if (emoji === '🤔') setEmoji('⚖️');
      setDecisionCategory('');
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!title.trim() || !sideA.trim() || !sideB.trim()) return;
    if (creatorSide !== 'A' && creatorSide !== 'B') {
      alert('Pick your side before you create the trial.');
      return;
    }

    const blockedMatch = containsBlockedPhrase(`${title} ${sideA} ${sideB}`);
    if (blockedMatch) {
      alert(`Please remove disallowed content ("${blockedMatch}") and try again.`);
      return;
    }

    if (looksLikePersonalAttack(title, sideA, sideB)) {
      alert('Please avoid personal attacks. Rephrase this trial and try again.');
      return;
    }

    setSaving(true);
    try {
      // ✅ Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        safeStorage.set('gweffit_return_url', '/create');
        window.location.href = '/?auth=signup&auth_context=create_trial&auth_message=Enter%20your%20email%20and%20we%27ll%20send%20a%206-digit%20code%20to%20post%20your%20trial.';
        setSaving(false);
        return;
      }

      // ✅ Fetch user profile by auth_user_id
      const { data: userRow, error: userErr } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', session.user.id)
        .single();

      if (userErr || !userRow?.id) {
        console.error('❌ Could not find user row:', userErr);
        alert('User profile not found. Please sign out and sign in again.');
        setSaving(false);
        return;
      }

      const confirmedUserId = userRow.id;

      const { data: validationResp, error: validationErr } = await supabase.rpc('validate_trial_content', {
        p_title: title.trim(),
        p_side_a: sideA.trim(),
        p_side_b: sideB.trim(),
      });

      if (validationErr) {
        console.warn('[safety] validate_trial_content_failed', validationErr);
      } else if (validationResp && validationResp.ok === false) {
        const reasonCode = validationResp.reason_code || 'blocked';
        const matched = validationResp.matched ? ` (${validationResp.matched})` : '';
        const pretty = reasonCode === 'personal_attack'
          ? 'Please avoid personal attacks. Rephrase and try again.'
          : reasonCode === 'self_harm'
            ? 'This topic is not allowed for safety reasons.'
            : reasonCode === 'doxxing'
              ? 'Please remove personal contact or private info (email/phone) and try again.'
              : reasonCode === 'sexual_minors'
                ? 'This content is not allowed for safety reasons.'
            : 'Please remove disallowed content and try again.';
        alert(`${pretty}${matched}`);
        setSaving(false);
        return;
      }

      const durationSeconds = getDurationSeconds();
      const isQuickTrial = durationSeconds <= 120;

      const { data, error } = await supabase.rpc('create_trial_with_limits', {
        p_title: title.trim(),
        p_side_a: sideA.trim(),
        p_side_b: sideB.trim(),
        p_creator_side: creatorSide,
        p_image_emoji: emoji,
        p_trial_type: isQuickTrial ? 'quick' : 'extended',
        p_trial_mode: trialMode,
        p_jury_size: BETA_MODE ? 6 : 12,
        p_time_limit_seconds: durationSeconds,
        p_expires_at: new Date(Date.now() + durationSeconds * 1000).toISOString(),
        p_status: 'filling',
        p_decision_category: decisionCategory || null,
      });

      if (error) throw error;
      if (!data?.ok) {
        if (data?.error === 'trial_rate_limited_daily') {
          alert('You’ve hit the daily trial limit (15 per 24 hours). Try again tomorrow.');
          setSaving(false);
          return;
        }
        if (data?.error === 'trial_rate_limited_burst') {
          alert('You’re creating trials too fast (max 5 per 10 minutes). Please wait and try again.');
          setSaving(false);
          return;
        }
        if (data?.error === 'trial_cooldown_60s') {
          alert('Slow down—wait ~60 seconds before posting another trial.');
          setSaving(false);
          return;
        }
        if (data?.error === 'trial_duplicate_24h') {
          alert('Looks like you already posted that one recently. Try a new angle or edit the wording.');
          setSaving(false);
          return;
        }
        if (data?.error === 'trial_content_blocked') {
          const reasonCode = data?.reason_code || 'blocked';
          const pretty = reasonCode === 'personal_attack'
            ? 'Please avoid personal attacks. Rephrase and try again.'
            : reasonCode === 'self_harm'
              ? 'This topic is not allowed for safety reasons.'
              : reasonCode === 'doxxing'
                ? 'Please remove personal contact or private info (email/phone) and try again.'
                : reasonCode === 'sexual_minors'
                  ? 'This content is not allowed for safety reasons.'
              : 'Please remove disallowed content and try again.';
          alert(pretty);
          setSaving(false);
          return;
        }
        if (data?.error === 'creator_side_required') {
          alert('Pick your side before you create the trial.');
          setSaving(false);
          return;
        }
        throw new Error(data?.error || 'trial_create_failed');
      }

      const verifiedTarget = await verifyCreatedTrialExists(data.trial_id, {
        authUserId: session.user.id,
        appUserId: confirmedUserId,
      });
      if (!verifiedTarget.ok) {
        console.warn('[create_trial_verify_missing]', {
          trial_id: data.trial_id,
          auth_user_id: session.user.id,
        });
        alert("We couldn't confirm that your trial was actually posted. Nothing was sent live. Please try again.");
        setSaving(false);
        return;
      }

      persistRecentCreatedTrial(data.trial_id);

      // 🔥 Update streak for actual activity (creating trial)
      await updateStreakForActivity(confirmedUserId);

      try {
        await recordReferralMilestone('first_trial_created');
      } catch (milestoneErr) {
        console.warn('⚠️ Failed to record referral first_trial_created milestone:', milestoneErr?.message || milestoneErr);
      }

      // ✅ Go to trial detail page (canonical short URL) with explicit creation confirmation
      window.location.href = `/t/${data.trial_id}?created=1`;
    } catch (err) {
      console.error('❌ Create trial failed:', err);
      const msg = String(err?.message || '');
      if (msg.includes('trial_cooldown_60s')) {
        alert('Slow down—wait ~60 seconds before posting another trial.');
        return;
      }
      if (msg.includes('trial_duplicate_24h')) {
        alert('Looks like you already posted that one recently. Try a new angle or edit the wording.');
        return;
      }
      if (msg.includes('trial_rate_limited_daily')) {
        alert('You’ve hit the daily trial limit (15 per 24 hours). Try again tomorrow.');
        return;
      }
      if (msg.includes('trial_rate_limited_burst')) {
        alert('You’re creating trials too fast (max 5 per 10 minutes). Please wait and try again.');
        return;
      }
      alert('Could not create trial. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const selectedDuration = DURATION_OPTIONS.find(o => o.value === duration);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border border-slate-700 bg-slate-800/80 p-6">
            <div className="text-sm font-black uppercase tracking-[0.16em] text-purple-300">Create Trial</div>
            <div className="mt-2 text-lg font-bold text-slate-50">Checking your account…</div>
            <div className="mt-1 text-sm text-slate-400">We&apos;ll take you into sign in if needed before you post.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          {BETA_MODE && (
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-yellow-500/20 border border-yellow-400/60 text-yellow-200 uppercase tracking-wide">
              Beta
            </span>
          )}
        </div>

        <div className="text-sm text-slate-400 mb-4">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2">/</span>
          <span>Create Trial</span>
        </div>

        <h1 className="text-3xl font-extrabold mb-6">Create New Trial</h1>

        <form onSubmit={handleCreate} className="space-y-5">
          {/* Trial Mode */}
          <div className="p-4 bg-slate-800 rounded-xl border border-slate-700">
            <label className="block text-sm font-semibold mb-2">Trial Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => switchTrialMode('debate')}
                className={`px-4 py-3 rounded-xl border-2 font-bold transition-all ${
                  trialMode === 'debate'
                    ? 'bg-purple-600 border-purple-500 scale-[1.01]'
                    : 'bg-slate-900/40 border-slate-700 hover:border-slate-600'
                }`}
                aria-pressed={trialMode === 'debate'}
              >
                Debate
              </button>
              <button
                type="button"
                onClick={() => switchTrialMode('decide_for_me')}
                className={`px-4 py-3 rounded-xl border-2 font-bold transition-all ${
                  trialMode === 'decide_for_me'
                    ? 'bg-yellow-500/20 border-yellow-400/60 scale-[1.01] text-yellow-100'
                    : 'bg-slate-900/40 border-slate-700 hover:border-slate-600'
                }`}
                aria-pressed={trialMode === 'decide_for_me'}
              >
                Decide for me
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              {trialMode === 'decide_for_me'
                ? 'High-stakes personal decisions. You’ll want to share this with your crew.'
                : 'Classic A vs B debate. Let the jury settle it.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              {trialMode === 'decide_for_me' ? 'What do you need help deciding?' : 'Trial Question'}
            </label>
            <input
              className="w-full bg-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-white"
              placeholder={trialMode === 'decide_for_me' ? 'e.g., Should I text my ex?' : "e.g., Who's the real GOAT?"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            {trialMode === 'decide_for_me' && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-300 mb-2">Quick templates</div>
                <div className="flex flex-wrap gap-2">
                  {DECIDE_FOR_ME_TEMPLATES.map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTitle(t)}
                      className="px-3 py-2 rounded-full bg-slate-700/60 hover:bg-slate-700 text-xs font-semibold border border-slate-600"
                    >
                      {t}
                    </button>
                  ))}
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold mb-2">Category</label>
                  <select
                    className="w-full bg-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-white border border-slate-700"
                    value={decisionCategory}
                    onChange={(e) => setDecisionCategory(e.target.value)}
                  >
                    {DECISION_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    Optional — helps us generate better “verdict” copy later.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-2">Side A</label>
              <input
                className="w-full bg-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-white"
                placeholder={trialMode === 'decide_for_me' ? 'e.g., Do it' : 'e.g., Messi'}
                value={sideA}
                onChange={(e) => setSideA(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Side B</label>
              <input
                className="w-full bg-slate-800 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-purple-500 text-white"
                placeholder={trialMode === 'decide_for_me' ? "e.g., Don't do it" : 'e.g., Ronaldo'}
                value={sideB}
                onChange={(e) => setSideB(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
            <label className="block text-sm font-semibold mb-2">Your Call</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCreatorSide('A')}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${
                  creatorSide === 'A'
                    ? 'border-purple-400 bg-purple-500/15 text-white'
                    : 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-slate-600'
                }`}
                aria-pressed={creatorSide === 'A'}
              >
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">You’re backing</div>
                <div className="mt-1 font-black">{sideA.trim() || 'Side A'}</div>
              </button>
              <button
                type="button"
                onClick={() => setCreatorSide('B')}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${
                  creatorSide === 'B'
                    ? 'border-amber-300 bg-amber-500/15 text-white'
                    : 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-slate-600'
                }`}
                aria-pressed={creatorSide === 'B'}
              >
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">You’re backing</div>
                <div className="mt-1 font-black">{sideB.trim() || 'Side B'}</div>
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Your call stays hidden while the trial is live. It shows up later as your receipt.
            </p>
          </div>

          {trialMode === 'decide_for_me' && (
            <p className="text-xs text-slate-500 -mt-2">
              Tip: You can customize the labels (Yes/No, Stay/Leave, Send it/Don’t).
            </p>
          )}

          <div>
            <label className="block text-sm font-semibold mb-2">Trial Emoji</label>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map(e => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEmoji(e)}
                  className={`px-4 py-3 rounded-xl border-2 transition-all ${
                    emoji === e
                      ? 'bg-purple-600 border-purple-500 scale-110'
                      : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  }`}
                  aria-pressed={emoji === e}
                >
                  <span className="text-xl">{e}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Duration Picker - Beta Only */}
          {BETA_MODE && (
            <div>
              <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-400" />
                Trial Duration
                <span className="text-xs font-normal text-slate-400">(Beta feature)</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {DURATION_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDuration(option.value)}
                    className={`px-3 py-3 rounded-xl border-2 transition-all text-center ${
                      duration === option.value
                        ? 'bg-purple-600 border-purple-500 scale-105'
                        : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                    }`}
                    aria-pressed={duration === option.value}
                  >
                    <div className="font-bold text-sm">{option.label}</div>
                  </button>
                ))}
              </div>
              {selectedDuration && (
                <p className="mt-2 text-xs text-slate-400">
                  💡 {selectedDuration.description}
                </p>
              )}
            </div>
          )}

          <CommunityRulesPanel title="Community Rules (quick read)" />

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 font-black text-lg rounded-xl px-4 py-4 transition-all transform hover:scale-105"
          >
            {saving ? 'Creating Trial...' : '⚖️ Create Trial'}
          </button>
        </form>

        <div className="mt-8 p-4 bg-slate-800 rounded-xl border border-slate-700">
          <div className="text-sm text-slate-300">
            <div className="font-bold text-white mb-2">
              {BETA_MODE ? '🧪 Beta Trial' : '⚡ Quick Trial'} - Your trial will:
            </div>
            <ul className="list-disc ml-5 space-y-1 text-slate-300">
              {BETA_MODE ? (
                <>
                  <li>Run for the selected duration</li>
                  <li>Need {BETA_MODE ? 6 : 12} jurors to vote for final verdict</li>
                  <li>Generate a shareable verdict meme when complete</li>
                  <li>Earn you Creator XP points</li>
                </>
              ) : (
                <>
                  <li>Start with a 2-minute countdown timer</li>
                  <li>Auto-extend by 2 minutes if 12 jurors haven&apos;t voted yet</li>
                  <li>Need 12 jurors to vote for final verdict</li>
                  <li>Generate a shareable verdict meme when complete</li>
                  <li>Earn you Creator XP points</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
